// [CaseDetails]/index.tsx
import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Badge, Card, Container, Group, Loader, Center, Divider,
  Stack, Text, Title, SimpleGrid, Button, Modal, ActionIcon, Tooltip, Box,
  Menu,
} from '@mantine/core';
import {
  IconBriefcase2, IconClockHour4, IconFileText,
  IconFiles, IconUsers, IconArrowLeft, IconPencil,
  IconInfoCircle, IconRefresh,
  IconBriefcase,
  IconChevronDown,
  IconHeartHandshake, IconMap, IconMap2, IconMapPin,
} from '@tabler/icons-react';
import { brandBadgeStyle } from '@/utils/color';
import { badgeStyles } from '@/utils/tableStyles';
import Tabs from '@/components/ui/navigation/Tabs';
import {
   useACCounsellingServices, CareCase,
  useCareNeedService, useCareZoneService,
  useLocationService, useCareAreaService, useLeaderService,
  useCareCaseStatusService, CareCaseStatus,
} from '@actschurch/ccms-sdk';
import { useAuth } from '@/hooks/useEnrichedAuth';
import { tabStyle } from '@/components/ui/TabStyle';
import { toast } from '@/components/ui/providers/AlertProvider';

import NotesTab       from './Tabs/NotesTab';
import SessionTab     from './Tabs/SessionTab';
import CounseleesTab from './Tabs/CounseleesTab';
import AnswersTab from './Tabs/AnswersTab';
import FilesTab       from './Tabs/FilesTab';
import EditTab        from './Tabs/EditTab';
import LeadersTab from './Tabs/LeaderTab';
import Avatar from '@/components/ui/data-display/Avatar';

function formatSDKDate(value: number | string | null | undefined): string {
  const raw = String(value ?? '');
  if (raw.length < 8) return '—';
  const date = `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
  if (raw.length >= 12) return `${date} ${raw.slice(8, 10)}:${raw.slice(10, 12)}`;
  return date;
}

interface LookupMaps {
  needs:     Map<number, string>;
  zones:     Map<number, string>;
  locations: Map<number, string>;
  areas:     Map<number, string>;
  leaders:   Map<string, string>;
}

const EMPTY_MAPS: LookupMaps = {
  needs: new Map(), zones: new Map(),
  locations: new Map(), areas: new Map(), leaders: new Map(),
};

/**
 * Icon + label + value row, the same shape the leader and facilitator Overview tabs use.
 * Renders "Not set" rather than disappearing, so the two columns stay aligned.
 */
function DetailRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  const empty = value === null || value === undefined || value === '';
  return (
    <Group gap="sm" wrap="nowrap" align="flex-start">
      <div style={{ color: 'var(--mantine-color-dimmed)', marginTop: 2 }}>{icon}</div>
      <Stack gap={2} style={{ minWidth: 0 }}>
        <Text size="xs" c="dimmed">{label}</Text>
        {empty
          ? <Text size="sm" c="dimmed">Not set</Text>
          : <Text size="sm" fw={600} style={{ wordBreak: 'break-word' }}>{value}</Text>}
      </Stack>
    </Group>
  );
}

export default function CaseDetailsPage() {
  const { id }   = useParams<{ id: string }>();
  const navigate = useNavigate();
  const caseID   = Number(id);

  const counsellingService = useACCounsellingServices();
  const CareNeeds          = useCareNeedService();
  const CareZones          = useCareZoneService();
  const Locations          = useLocationService();
  const CareAreas          = useCareAreaService();
  const Leaders            = useLeaderService();
  const CaseStatuses = useCareCaseStatusService();
  const { user } = useAuth();

  const [caseData, setCaseData]   = useState<CareCase | null>(null);
  const [maps, setMaps]           = useState<LookupMaps>(EMPTY_MAPS);
  const [loading, setLoading]     = useState(true);
  const [activeTab, setActiveTab] = useState<string | null>('case');
  const [editOpen, setEditOpen]   = useState(false);
  
 

const [statuses, setStatuses]             = useState<CareCaseStatus[]>([]);
const [loadingStatuses, setLoadingStatuses] = useState(false);
const [changingStatus, setChangingStatus]   = useState(false);

  const fetchLookups = useCallback(async () => {
    try {
      const [needs, zones, locs, areas, leaders] = await Promise.all([
        CareNeeds.getAll(), CareZones.getAll(), Locations.getAll(),
        CareAreas.getAll(), Leaders.list(),
      ]);
      setMaps({
        needs:     new Map((needs   ?? []).map((n: any) => [n.needID,     n.name ?? `Need ${n.needID}`])),
        zones:     new Map((zones   ?? []).map((z: any) => [z.zoneID,     z.name ?? `Zone ${z.zoneID}`])),
        locations: new Map((locs    ?? []).map((l: any) => [l.locationID, l.name ?? l.locationName ?? `Location ${l.locationID}`])),
        areas:     new Map((areas   ?? []).map((a: any) => [a.areaID,     a.name ?? `Area ${a.areaID}`])),
        leaders:   new Map((leaders ?? []).map((l: any) => {
          const name = [l.firstName, l.surname].filter(Boolean).join(' ').trim()
            || l.fullName?.trim() || l.displayName?.trim() || l.userId;
          return [l.userId, name];
        })),
      });
    } catch (e) {
      console.error('Error fetching lookups:', e);
    }
  }, [CareNeeds, CareZones, Locations, CareAreas, Leaders]);

  const fetchCase = useCallback(async (showLoading = true) => {
    if (!caseID) return;
    if (showLoading) setLoading(true);
    try {
      const data = await counsellingService.careCase.getById(caseID);
      setCaseData(data);
    } catch (e) {
      console.error('Error fetching case:', e);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [caseID, counsellingService]);

  const fetchStatuses = useCallback(async () => {
  setLoadingStatuses(true);
  try {
    setStatuses(await CaseStatuses.getAll());
  } catch (e) {
    console.error('Error fetching case statuses:', e);
    toast.error('Error', 'Could not load case statuses.');
  } finally {
    setLoadingStatuses(false);
  }
}, [CaseStatuses]);

const handleStatusChange = async (newStatusID: number) => {
  if (!caseData || newStatusID === caseData.statusID) return;

  setChangingStatus(true);
  try {
    await counsellingService.careCase.changeStatus(caseID, newStatusID);
    toast.success('Status Updated', 'Case status changed.');
    await fetchCase();
  } catch (e) {
    console.error('Error changing case status:', e);
    toast.error('Error', 'Failed to change status.');
  } finally {
    setChangingStatus(false);
  }
};

useEffect(() => { fetchStatuses(); }, [fetchStatuses]);

  const handleRefresh = useCallback(async () => {
    if (!caseID) return;
    setLoading(true);
    try {
      await Promise.all([fetchLookups(), fetchCase(false)]);
    } catch (e) {
      console.error('Error refreshing case details:', e);
    } finally {
      setLoading(false);
    }
  }, [caseID, fetchLookups, fetchCase]);

  useEffect(() => { fetchLookups(); }, [fetchLookups]);
  useEffect(() => { fetchCase();    }, [fetchCase]);

  const needName     = caseData?.needID     ? (maps.needs.get(caseData.needID)         ?? `Need ${caseData.needID}`)         : null;
  const areaName     = caseData?.areaID     ? (maps.areas.get(caseData.areaID)         ?? `Area ${caseData.areaID}`)         : null;
  const zoneName     = caseData?.zoneID     ? (maps.zones.get(caseData.zoneID)         ?? `Zone ${caseData.zoneID}`)         : null;
  const locationName = caseData?.locationID ? (maps.locations.get(caseData.locationID) ?? `Location ${caseData.locationID}`) : null;
  const leaderName   = caseData?.userId     ? (maps.leaders.get(caseData.userId)       ?? caseData.userId)                   : null;
  const currentStatus = caseData?.statusID ? statuses.find((s) => s.statusID === caseData.statusID) : null;
  const statusName   = currentStatus?.name ?? (caseData?.statusID ? `Status ${caseData.statusID}` : null);
  const statusColor  = caseData?.isActive ? 'green' : 'red';
  const statusLabel  = caseData?.isActive ? 'Active' : 'Inactive';

  if (loading) {
    return (
      <Center py="xl">
        <Stack align="center" gap="sm">
          <Loader size="lg" />
          <Text size="sm" c="dimmed">Loading case details…</Text>
        </Stack>
      </Center>
    );
  }

  if (!caseData) {
    return <Center py="xl"><Text c="dimmed">Case not found.</Text></Center>;
  }

  return (
    <Container size="xl" py="xl">
       <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>

      {/* ── Page header: Back + Title + Edit button ── */}
      <Group justify="space-between" align="center" mb="md">
        <Group gap="sm">
          <Title order={2}>Case Details</Title>
        </Group>
        <Group gap="xs" align="center">
            <Tooltip label="Refresh" withArrow>
              <ActionIcon
                variant="subtle"
                onClick={handleRefresh}
                loading={loading}
                size="lg"
              >
                <IconRefresh size={16} />
              </ActionIcon>
            </Tooltip>

            {/* Edit lives on the profile card, where the leader and facilitator pages put it.
                Refresh and Change Status stay here in the page header. */}
            <Menu shadow="md" width={220} position="bottom-end">
              <Menu.Target>
                <Button
                  variant="default"
                  rightSection={changingStatus ? <Loader size={14} /> : <IconChevronDown size={14} />}
                  disabled={loadingStatuses || changingStatus}
                >
                  {caseData.statusID
                    ? statuses.find((s) => s.statusID === caseData.statusID)?.name ?? 'Change status'
                    : 'Change status'}
                </Button>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Label>Change status</Menu.Label>

                {loadingStatuses ? (
                  <Center py="sm">
                    <Loader size="xs" />
                  </Center>
                ) : (
                  statuses.map((s) => (
                    <Menu.Item
                      key={s.statusID}
                      onClick={() => handleStatusChange(s.statusID)}
                      disabled={s.statusID === caseData.statusID}
                      leftSection={
                        s.color ? (
                          <div
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              backgroundColor: s.color,
                              flexShrink: 0,
                            }}
                          />
                        ) : null
                      }
                    >
                      {s.name ?? `Status ${s.statusID}`}
                    </Menu.Item>
                  ))
                )}
              </Menu.Dropdown>
            </Menu>
          </Group>
      </Group>

      {/* ── Edit modal ── */}
      <Modal
        opened={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit Case"
        centered
        size="xl"
        padding="lg"
      >
        <EditTab
          caseID={caseID}
          caseData={caseData}
          active={editOpen}
          onCaseChange={() => {
            fetchCase();
            setEditOpen(false);
          }}
        />
      </Modal>

      {/* ── Profile card ── */}
      <Card withBorder radius="md" padding="lg" mb="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group gap="md" align="flex-start" wrap="nowrap" style={{ flex: 1 }}>
            <Avatar radius="md" size={105}>  
                <IconBriefcase size={60} />
             </Avatar>

            <Stack gap={4} style={{ flex: 1 }}>
              <Title order={4}>{caseData.name ?? '—'}</Title>

              {caseData.description && (
                <Box
                  style={{ flex: 1 }}
                  dangerouslySetInnerHTML={{ __html: caseData.description || '' }}
                />
              )}

              {/* One badge row, Active/Inactive included, matching the leader and facilitator
                  profile cards. The standalone pill that used to sit top-right is gone: that
                  corner is the primary action's. */}
              <Group gap={6}>
                {statusName && (
                  <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(currentStatus?.color)}>
                    {statusName}
                  </Badge>
                )}
                {needName && (
                  <Badge variant="outline" color="gray">{needName}</Badge>
                )}
                <Badge color={statusColor} variant="filled">{statusLabel}</Badge>
              </Group>

              {leaderName && (
                <Text size="sm" c="dimmed" mt={2}>
                  <strong>Leader:</strong> {leaderName}
                </Text>
              )}
            </Stack>
          </Group>

          <Button
            variant="filled"
            leftSection={<IconPencil size={16} />}
            onClick={() => setEditOpen(true)}
            disabled={!caseData}
          >
            Edit Case
          </Button>
        </Group>
      </Card>

      {/* ── Tabs (no edit tab) ── */}
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="case"        leftSection={<IconInfoCircle size={16} />} style={tabStyle(activeTab === 'case')}>Overview</Tabs.Tab>
          <Tabs.Tab value="notes"       leftSection={<IconFileText   size={16} />} style={tabStyle(activeTab === 'notes')}>Notes</Tabs.Tab>
          <Tabs.Tab value="session"     leftSection={<IconClockHour4 size={16} />} style={tabStyle(activeTab === 'session')}>Session</Tabs.Tab>
          <Tabs.Tab value="leaders" leftSection={<IconUsers size={16} />} style={tabStyle(activeTab === 'leaders')}>Leaders</Tabs.Tab>
          <Tabs.Tab value="counselees" leftSection={<IconUsers      size={16} />} style={tabStyle(activeTab === 'counselees')}>Counselees</Tabs.Tab>
          <Tabs.Tab value="answers" leftSection={<IconFileText size={16} />} style={tabStyle(activeTab === 'answers')}>Answers</Tabs.Tab>
          <Tabs.Tab value="files"       leftSection={<IconFiles      size={16} />} style={tabStyle(activeTab === 'files')}>Files</Tabs.Tab>
        </Tabs.List>

        {/* All tab panels share one bordered card, matching the leader and facilitator detail pages.
            Panels carry no inner heading: the tab label already names the section. */}
        <Card withBorder radius="md" padding="lg" mt="sm">

          {/* Two bordered cards, the same shape as the leader and facilitator Overview tabs. */}
          <Tabs.Panel value="case" pt="md">
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
              <Card withBorder radius="md" padding="lg">
                <Text fw={700} mb="sm">Case Details</Text>
                <Divider mb="md" />
                <Stack gap="md">
                  <DetailRow
                    icon={<IconBriefcase2 size={18} />}
                    label="Case ID"
                    value={caseData.caseID}
                  />
                  <DetailRow
                    icon={<IconInfoCircle size={18} />}
                    label="Status"
                    value={
                      statusName
                        ? (
                          <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(currentStatus?.color)}>
                            {statusName}
                          </Badge>
                        )
                        : null
                    }
                  />
                  <DetailRow
                    icon={<IconHeartHandshake size={18} />}
                    label="Need"
                    value={needName}
                  />
                  <DetailRow
                    icon={<IconClockHour4 size={18} />}
                    label="Date Opened"
                    value={formatSDKDate(caseData.caseDateTime)}
                  />
                </Stack>
              </Card>

              <Card withBorder radius="md" padding="lg">
                <Text fw={700} mb="sm">Assignment</Text>
                <Divider mb="md" />
                <Stack gap="md">
                  <DetailRow
                    icon={<IconUsers size={18} />}
                    label="Leader"
                    value={leaderName}
                  />
                  <DetailRow
                    icon={<IconMapPin size={18} />}
                    label="Location"
                    value={locationName}
                  />
                  <DetailRow
                    icon={<IconMap size={18} />}
                    label="Zone"
                    value={zoneName}
                  />
                  <DetailRow
                    icon={<IconMap2 size={18} />}
                    label="Area"
                    value={areaName}
                  />
                </Stack>
              </Card>
            </SimpleGrid>
          </Tabs.Panel>

          <Tabs.Panel value="notes" pt="md">
            <NotesTab
              caseID={caseID}
              active={activeTab === 'notes'}
              currentUserId={user?.id ?? undefined}
            />
          </Tabs.Panel>

          <Tabs.Panel value="session" pt="md">
            <SessionTab
              caseID={caseID}
              active={activeTab === 'session'}
              currentUserId={user?.id ?? undefined}
            />
          </Tabs.Panel>

          <Tabs.Panel value="leaders" pt="md">
            <LeadersTab caseID={caseID} active={activeTab === 'leaders'} onCaseChange={fetchCase} />
          </Tabs.Panel>

          <Tabs.Panel value="counselees" pt="md">
            <CounseleesTab
              caseID={caseID}
              active={activeTab === 'counselees'}
              onCaseChange={fetchCase}
              requestID={caseData?.requestID}
            />
          </Tabs.Panel>

          <Tabs.Panel value="answers" pt="md">
            <AnswersTab caseID={caseID} active={activeTab === 'answers'} />
          </Tabs.Panel>

          <Tabs.Panel value="files" pt="md">
            <FilesTab caseID={caseID} active={activeTab === 'files'} />
          </Tabs.Panel>

        </Card>
      </Tabs>
    </Container>
  );
}